/**
 * Boundary condition component.
 * Sets fixed pressure, fixed mass flow, or fixed pressure+temperature.
 *
 * N9 CONTRACT FIX: this component returns PURE deltas (deltaP = P_target − P,
 * deltaH = h_target − h) WITHOUT mutating the junction — matching the
 * component contract used everywhere else (the network applies deltas once).
 * The previous implementation mutated `inlet.P = P` AND returned
 * deltaP = P − P_old, so nsNetwork's `junction.P += deltaP` landed the
 * junction at 2P − P_old — a two-cycle oscillation whenever P_init ≠ P.
 */
import type { NSComponent, Junction, ComponentUpdate } from '../types';
import { propsFromPT_SI } from '../core/fluidAdapter';

export class NSBoundary implements NSComponent {
  id: string;
  type = 'boundary';
  params: Record<string, any>;

  constructor(id: string, params: { P?: number; T?: number; mdot?: number }) {
    this.id = id;
    this.params = params;
  }

  applyBoundaryConditions(inlet: Junction, _outlet: Junction, _dt: number): ComponentUpdate {
    const { P, T, mdot } = this.params;

    let deltaP = 0;
    let deltaH = 0;

    // Target pressure: report the delta only — the network applies it
    // (junction.P += deltaP lands exactly on P; outlet cells get
    // junction.P + deltaP = P when applied through Step 4).
    if (P != null) {
      deltaP = P - inlet.P;
    }

    // Target enthalpy from temperature — use specified P or current junction P.
    if (T != null) {
      const useP = P ?? inlet.P;
      if (useP > 0) {
        try {
          const props = propsFromPT_SI(useP, T);
          deltaH = props.h - inlet.h;
        } catch { /* property lookup failed — leave enthalpy unchanged */ }
      }
    }

    return {
      deltaP, deltaH,
      mdot: mdot ?? undefined,
      momentumSource: 0,
      energySource: 0,
      // Metadata reports the TARGET state this BC drives toward.
      metadata: { P: P ?? inlet.P, h: inlet.h + deltaH },
    };
  }
}
